import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plug, Loader2, Pencil, RotateCcw } from 'lucide-react';
import Button from '../components/ui/Button';
import { API_PATH, request } from '../config/api';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import { useAuth } from '../store/AuthContext';
import { formatDateTime } from '../utils/formatDate';
import styles from './IntegrationsPage.module.css';

const GROUPS = [
    { title: 'Email', keys: ['EMAIL_USER', 'EMAIL_PASS', 'RECEIVER_EMAIL'] },
    { title: 'Google OAuth', keys: ['GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_SECRET'] },
    { title: 'Microsoft OAuth', keys: ['MICROSOFT_CLIENT_ID', 'MICROSOFT_CLIENT_SECRET'] },
    { title: 'Cloudinary', keys: ['CLOUDINARY_CLOUD_NAME', 'CLOUDINARY_API_KEY', 'CLOUDINARY_API_SECRET'] },
    { title: 'AI (OpenAI)', keys: ['OPENAI_API_KEY'] },
    { title: 'Payments (Pay India)', keys: ['PAYINDIA_API_KEY', 'PAYINDIA_API_SECRET', 'PAYINDIA_WEBHOOK_SECRET', 'PAYINDIA_BASE_URL'] },
];

const LABELS = {
    EMAIL_USER: 'Email address',
    EMAIL_PASS: 'Email app password',
    RECEIVER_EMAIL: 'Contact form recipient',
    GOOGLE_CLIENT_ID: 'Client ID',
    GOOGLE_CLIENT_SECRET: 'Client secret',
    MICROSOFT_CLIENT_ID: 'Client ID',
    MICROSOFT_CLIENT_SECRET: 'Client secret',
    CLOUDINARY_CLOUD_NAME: 'Cloud name',
    CLOUDINARY_API_KEY: 'API key',
    CLOUDINARY_API_SECRET: 'API secret',
    OPENAI_API_KEY: 'API key',
    PAYINDIA_API_KEY: 'API key',
    PAYINDIA_API_SECRET: 'API secret',
    PAYINDIA_WEBHOOK_SECRET: 'Webhook secret',
    PAYINDIA_BASE_URL: 'Gateway base URL',
};

const IntegrationsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isSuperadmin = user?.role === 'superadmin';

    const [secrets, setSecrets] = useState({});
    const [audit, setAudit] = useState([]);
    const [auditError, setAuditError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [savedNote, setSavedNote] = useState('');

    const fetchAll = useCallback(async () => {
        if (!isSuperadmin) return;
        setLoading(true);
        setError(null);
        try {
            const [secretsRes, auditRes] = await Promise.all([
                request(`${API_PATH}/superadmin/settings/secrets`),
                request(`${API_PATH}/superadmin/settings/secrets/audit`),
            ]);
            const secretsData = await secretsRes.json().catch(() => ({}));
            const auditData = await auditRes.json().catch(() => ({}));
            if (!secretsRes.ok) {
                setError(secretsData.error || 'Failed to load integrations');
                return;
            }
            const byKey = {};
            (secretsData.secrets || []).forEach((s) => { byKey[s.key] = s; });
            setSecrets(byKey);
            if (auditRes.ok) {
                setAudit(auditData.entries || []);
                setAuditError(false);
            } else {
                setAudit([]);
                setAuditError(true);
            }
        } catch (e) {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    }, [isSuperadmin]);

    useEffect(() => {
        if (!savedNote) return;
        const t = setTimeout(() => setSavedNote(''), 6000);
        return () => clearTimeout(t);
    }, [savedNote]);

    useEffect(() => {
        if (!isSuperadmin) {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchAll();
    }, [isSuperadmin, navigate, fetchAll]);

    const openEdit = (key) => {
        setEditingKey(key);
        setEditValue('');
        setSaveError('');
        setSavedNote('');
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditValue('');
        setSaveError('');
        setSavedNote('');
    };

    const handleSave = async (key) => {
        const trimmed = editValue.trim();
        if (!trimmed) {
            setSaveError('Enter a value');
            return;
        }
        setSaving(true);
        setSaveError('');
        try {
            const res = await request(`${API_PATH}/superadmin/settings/secrets/${key}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: trimmed }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setSaveError(data.error || 'Failed to save');
                return;
            }
            setEditingKey((cur) => (cur === key ? null : cur));
            setEditValue((cur) => (editingKey === key ? '' : cur));
            const wasSecret = secrets[key]?.is_secret !== false;
            setSavedNote(
                wasSecret
                    ? `Saved ${LABELS[key] || key} — ends in ${data.masked_value ?? '••••'}`
                    : `Saved ${LABELS[key] || key} — now ${data.masked_value ?? ''}`
            );
            fetchAll();
        } catch (e) {
            setSaveError('Network error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async (key) => {
        const label = LABELS[key] || key;
        if (!window.confirm(`Reset ${label} to the .env default? This takes effect immediately.`)) return;
        setSavedNote('');
        try {
            const res = await request(`${API_PATH}/superadmin/settings/secrets/${key}`, { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'Failed to reset');
                return;
            }
            fetchAll();
        } catch (e) {
            setError('Network error');
        }
    };

    if (!isSuperadmin) return null;

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={Plug}
                title="Integrations"
                subtitle="Manage credentials for email, OAuth, uploads, AI, and payments. Changes take effect immediately, without a restart — except enabling Cloudinary for the first time, which needs a restart."
            />

            {error && <div className={styles.errorBanner} role="alert">{error}</div>}
            {savedNote && <div className={styles.savedBanner} role="status">{savedNote}</div>}

            {loading ? (
                <div className={styles.loading}>
                    <Loader2 size={32} className={styles.spinner} />
                    Loading…
                </div>
            ) : (
                <>
                    {GROUPS.map((group) => (
                        <section key={group.title} className={styles.group}>
                            <h2 className={styles.groupTitle}>{group.title}</h2>
                            <div className={styles.rows}>
                                {group.keys.map((key) => {
                                    const s = secrets[key] || {};
                                    const isEditing = editingKey === key;
                                    return (
                                        <div key={key} className={styles.row}>
                                            <div className={styles.rowInfo}>
                                                <span className={styles.rowLabel}>{LABELS[key] || key}</span>
                                                {!isEditing && (
                                                    <span className={styles.rowValue}>
                                                        {s.is_set_in_db
                                                            ? `${s.masked_value} (override)`
                                                            : s.masked_value
                                                                ? `${s.masked_value} (using .env default)`
                                                                : 'Not set — no .env default'}
                                                    </span>
                                                )}
                                            </div>
                                            {isEditing ? (
                                                <div className={styles.editRow}>
                                                    <input
                                                        type={s.is_secret === false ? 'text' : 'password'}
                                                        className={styles.input}
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        placeholder={`New ${LABELS[key] || key}`}
                                                        aria-label={`New value for ${group.title} ${LABELS[key] || key}`}
                                                        autoComplete="new-password"
                                                        spellCheck={false}
                                                        data-lpignore="true"
                                                        data-1p-ignore
                                                        disabled={saving}
                                                        autoFocus
                                                    />
                                                    <Button variant="primary" size="sm" onClick={() => handleSave(key)} disabled={saving}>
                                                        {saving ? 'Saving…' : 'Save'}
                                                    </Button>
                                                    <Button variant="secondary" size="sm" onClick={cancelEdit} disabled={saving}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className={styles.rowActions}>
                                                    <button type="button" className={styles.iconBtn} onClick={() => openEdit(key)} aria-label={`Edit ${group.title} ${LABELS[key] || key}`} title="Edit" disabled={!secrets[key]}>
                                                        <Pencil size={14} />
                                                    </button>
                                                    {s.is_set_in_db && (
                                                        <button type="button" className={styles.iconBtn} onClick={() => handleReset(key)} aria-label={`Reset ${group.title} ${LABELS[key] || key} to .env default`} title="Reset to .env default">
                                                            <RotateCcw size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {isEditing && saveError && <p className={styles.formError} role="alert">{saveError}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    ))}

                    <section className={styles.group}>
                        <h2 className={styles.groupTitle}>Audit log</h2>
                        {auditError ? (
                            <p className={styles.formError} role="alert">Couldn't load the audit log.</p>
                        ) : audit.length === 0 ? (
                            <p className={styles.emptyHint}>No changes yet.</p>
                        ) : (
                            <ul className={styles.auditList}>
                                {audit.map((entry) => (
                                    <li key={entry.id} className={styles.auditItem}>
                                        <span className={styles.auditText}>
                                            {entry.changed_by_name} {entry.action === 'updated' ? 'updated' : 'reset'} <strong>{entry.key}</strong>
                                        </span>
                                        <span className={styles.auditTime}>{formatDateTime(entry.changed_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default IntegrationsPage;
